function initializeRoomPlanner(root) {
  'use strict';
  const controller = new AbortController();
  const listen = (target, type, handler) => target.addEventListener(type, handler, {signal: controller.signal});
  const find = selector => root.querySelector(selector);
  const all = selector => [...root.querySelectorAll(selector)];
  const frame = find('[data-frame]');
  const stage = find('[data-stage]');
  const lamp = find('[data-lamp]');
  const roomPhoto = find('[data-room-photo]');
  const lampImage = find('[data-lamp-image]');
  const status = find('[data-status]');
  const error = find('[data-error]');
  const sizeInput = find('[data-size-input]');
  const brightnessInput = find('[data-brightness-input]');
  const lightOverlay = find('[data-light-overlay]');
  const input = find('[data-file-input]');
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const state = {product:null,variant:null,room:null,roomUrl:null,roomAspect:1.5,x:23,y:93,size:60,brightness:60,original:false,uploadVersion:0,assetVersion:0,assetReady:false};
  const pointers = new Map();
  let gesture = null;

  function say(message) { status.textContent = message; }
  function fail(message) { error.textContent = message; error.hidden = false; }
  function clearError() { error.hidden = true; error.textContent = ''; }
  function setReady() {
    const roomReady = Boolean(state.room);
    all('[data-action="original"],[data-action="reset"],[data-action="smaller"],[data-action="larger"],[data-action="dimmer"],[data-action="brighter"]').forEach(button => { button.disabled = !roomReady; });
    find('[data-action="save"]').disabled = !roomReady || !state.assetReady;
    find('[data-action="remove"]').hidden = !roomReady;
    if (state.product) find('[data-upload-label]').textContent = roomReady ? state.product.labels.change : state.product.labels.upload;
    sizeInput.disabled = !roomReady;
    brightnessInput.disabled = !roomReady;
    root.dataset.roomReady = String(roomReady);
    root.dataset.assetReady = String(state.assetReady);
    all('[data-step]').forEach(item => item.classList.toggle('active', Number(item.dataset.step) === (roomReady ? 2 : 1)));
    renderLight();
    invalidateAI();
  }
  // A local illustration of uplight; the same drawing is used on screen and in exports.
  function drawLight(context, width, height) {
    if (!state.variant || !state.brightness) return;
    const glowColor = state.variant.glowColor || (state.variant.rgb ? '' : '#ffe7ad');
    if (!/^#[0-9a-f]{6}$/i.test(glowColor)) return;
    const color = [1,3,5].map(offset => parseInt(glowColor.slice(offset, offset + 2), 16));
    const edgeColor = glowColor.toLowerCase() === '#ffe7ad' ? [255,225,153] : color;
    const intensity = state.brightness / 100;
    const lampHeight = height * state.size / 100;
    const lampWidth = lampHeight * state.variant.width / state.variant.height;
    const headX = width * state.x / 100;
    const headY = height * (state.y - state.size) / 100;
    const glow = (x, y, radiusX, radiusY, opacity) => {
      context.save();
      context.translate(x, y);
      context.scale(radiusX, radiusY);
      const gradient = context.createRadialGradient(0, 0, 0, 0, 0, 1);
      gradient.addColorStop(0, `rgba(${color.join(',')},${opacity * intensity})`);
      gradient.addColorStop(.42, `rgba(${edgeColor.join(',')},${opacity * intensity * .55})`);
      gradient.addColorStop(1, `rgba(${edgeColor.join(',')},0)`);
      context.fillStyle = gradient;
      context.fillRect(-1, -1, 2, 2);
      context.restore();
    };
    if (state.product.kind === 'desk') {
      glow(headX, headY + lampHeight * .12, lampWidth * .55, lampHeight * .27, .28);
    } else if (state.product.kind === 'shade') {
      glow(headX + lampWidth * .14, headY + lampHeight * .12, lampWidth * .8, lampHeight * .25, .32);
    } else if (state.product.kind === 'spot') {
      glow(headX, headY + lampHeight * .15, lampWidth * 1.1, lampHeight * .28, .3);
    } else {
      glow(headX, headY - lampHeight * .16, lampWidth * 2.3, lampHeight * .45, .42);
      glow(headX, headY - lampHeight * .015, lampWidth * .82, lampHeight * .075, .54);
    }
  }
  function renderLight() {
    const context = lightOverlay.getContext('2d');
    context.clearRect(0, 0, lightOverlay.width, lightOverlay.height);
    if (state.room && state.assetReady) drawLight(context, lightOverlay.width, lightOverlay.height);
    brightnessInput.value = String(state.brightness);
    find('[data-brightness-value]').textContent = state.brightness ? `${state.brightness}%` : 'Off';
    root.dataset.brightness = String(state.brightness);
  }
  function placement() {
    const aspect = state.variant ? state.variant.width / state.variant.height : .22;
    const halfWidth = state.size / state.roomAspect * aspect / 2;
    state.x = clamp(state.x, halfWidth + 1, 99 - halfWidth);
    state.y = clamp(state.y, state.size + 1, 98);
    lamp.style.left = `${state.x}%`;
    lamp.style.top = `${state.y}%`;
    lamp.style.height = `${state.size}%`;
    lamp.style.setProperty('--lamp-aspect', aspect);
    const anchor = find('[data-anchor]');
    anchor.style.left = `${state.x}%`;
    anchor.style.top = `${state.y}%`;
    sizeInput.value = String(Math.round(state.size));
    find('[data-size-value]').textContent = `${Math.round(state.size)}%`;
    frame.classList.toggle('is-original', state.original);
    find('[data-action="original"]').setAttribute('aria-pressed', String(state.original));
    root.dataset.x = state.x.toFixed(3);
    root.dataset.y = state.y.toFixed(3);
    root.dataset.size = state.size.toFixed(3);
    renderLight();
    invalidateAI();
  }
  function reset() {
    state.x = 23; state.y = 93; state.size = 60; state.brightness = 60; state.original = false;
    placement();
  }
  function loadImage(source) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Image could not be opened.'));
      image.crossOrigin = 'anonymous';
      image.src = source;
    });
  }
  async function selectVariant(variant, {updateUrl = true} = {}) {
    const version = ++state.assetVersion;
    state.variant = variant;
    state.assetReady = false;
    root.dataset.selectedVariant = variant.id;
    root.dataset.selectedFinish = variant.title;
    all('[data-variant-id]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.variantId === variant.id)));
    find('[data-finish-name]').textContent = variant.title;
    find('[data-price]').textContent = variant.formattedPrice || new Intl.NumberFormat(document.documentElement.lang, {style:'currency', currency:state.product.currency}).format(variant.price / 100);
    find('[data-availability]').textContent = variant.available ? 'Available to order' : 'Currently unavailable';
    find('[data-variant-note]').textContent = variant.rgb ? state.product.labels.rgbNote : state.product.labels.whiteNote;
    find('[data-brightness-help]').textContent = variant.rgb ? state.product.labels.rgbGlowNote : state.product.labels.glowNote;
    const url = new URL(state.product.productUrl, location.origin);
    url.searchParams.set('variant', variant.id);
    all('[data-product-link],[data-shop-link]').forEach(link => { link.href = url.href; });
    find('[data-shop-link]').firstChild.textContent = variant.available ? `${state.product.labels.shop} ` : 'View this lamp ';
    find('[data-product-image]').src = variant.image;
    find('[data-product-image]').alt = `${state.product.title} - ${variant.title}`;
    if (updateUrl) {
      const localUrl = new URL(location.href);
      localUrl.searchParams.set('product', state.product.handle);
      localUrl.searchParams.set('variant', variant.id);
      history.replaceState(null, '', localUrl);
    }
    state.original = false;
    lampImage.hidden = true;
    setReady();
    placement();
    try {
      const image = await loadImage(variant.image);
      if (version !== state.assetVersion) return;
      lampImage.src = image.src;
      lampImage.hidden = false;
      state.assetReady = true;
      clearError();
      say(state.room ? `${variant.title} selected. Drag the lamp or adjust its size.` : 'Choose a photo or try the sample room to get started.');
    } catch {
      if (version !== state.assetVersion) return;
      fail('This finish could not load. Please choose another finish or reload the page.');
    }
    setReady();
  }
  async function openRoom(source, label, version) {
    const image = await loadImage(source);
    if (version !== state.uploadVersion) { if (source.startsWith('blob:')) URL.revokeObjectURL(source); return; }
    if (state.roomUrl?.startsWith('blob:')) URL.revokeObjectURL(state.roomUrl);
    state.room = image; state.roomUrl = source;
    state.roomAspect = image.naturalWidth / image.naturalHeight;
    const lightRatio = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
    lightOverlay.width = Math.round(image.naturalWidth * lightRatio);
    lightOverlay.height = Math.round(image.naturalHeight * lightRatio);
    roomPhoto.src = source;
    roomPhoto.alt = label;
    frame.style.setProperty('--room-aspect', state.roomAspect);
    frame.style.width = `min(100%, ${Math.round(620 * state.roomAspect)}px)`;
    frame.hidden = false;
    stage.classList.add('has-room');
    find('[data-empty]').hidden = true;
    find('[data-room-label]').textContent = label;
    reset(); setReady(); clearError();
    say('Your room is ready. Drag the lamp into position and adjust its size.');
  }
  function removeRoom() {
    ++state.uploadVersion;
    if (state.roomUrl?.startsWith('blob:')) URL.revokeObjectURL(state.roomUrl);
    state.room = null; state.roomUrl = null; state.roomAspect = 1.5;
    pointers.clear(); gesture = null;
    roomPhoto.removeAttribute('src');
    frame.hidden = true;
    stage.classList.remove('has-room');
    find('[data-empty]').hidden = false;
    find('[data-drop]').hidden = true;
    find('[data-room-label]').textContent = state.product.labels.room;
    input.value = '';
    reset(); setReady(); clearError();
    say('Photo removed. Your selected finish is kept. Upload a new room or try the sample.');
    find('[data-action="upload"]').focus();
  }
  async function useFile(file) {
    clearError();
    if (!file) return;
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) return fail('Please choose a JPG, PNG or WebP image.');
    if (file.size > 20 * 1024 * 1024) return fail('Please choose an image smaller than 20 MB.');
    const version = ++state.uploadVersion;
    say('Opening your room photo…');
    let objectUrl, bitmap;
    try {
      bitmap = await createImageBitmap(file, {imageOrientation:'from-image'});
      if (version !== state.uploadVersion) return;
      if (bitmap.width < 100 || bitmap.height < 100) throw new Error('too-small');
      const ratio = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * ratio); canvas.height = Math.round(bitmap.height * ratio);
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff'; context.fillRect(0,0,canvas.width,canvas.height);
      context.drawImage(bitmap,0,0,canvas.width,canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve,'image/jpeg',.9));
      if (!blob) throw new Error('conversion-failed');
      objectUrl = URL.createObjectURL(blob);
      await openRoom(objectUrl, 'Your room', version);
    } catch (exception) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (version === state.uploadVersion) {
        fail(exception.message === 'too-small' ? 'Choose a larger room photo, at least 100 × 100 pixels.' : 'We could not open that image. Try another JPG, PNG or WebP photo.');
        say(state.room ? 'Your previous room photo is still here.' : 'Choose a photo or try the sample room.');
      }
    } finally { bitmap?.close(); input.value = ''; }
  }
  async function useSample() {
    if (!state.product) return;
    const version = ++state.uploadVersion;
    say('Opening the sample room…');
    try { await openRoom(state.product.sampleImage, 'Sample living room', version); }
    catch { if (version === state.uploadVersion) { fail('The sample room could not load. You can still upload your own room photo.'); say('Try uploading your room photo.'); } }
  }
  // AI is requested only after an explicit click; initial placement stays local.
  const ai = {controller:null,version:0,busy:false,url:null,key:null,view:'placement'};
  const aiButton = find('[data-action="generate"]');
  const aiImage = find('[data-ai-image]');
  const aiStatus = find('[data-ai-status]');
  const aiCompare = find('[data-ai-compare]');
  let aiEndpoint = null;
  if (root.dataset.aiEndpoint) {
    const candidate = new URL(root.dataset.aiEndpoint, location.origin);
    if (candidate.origin === location.origin) aiEndpoint = candidate.href;
  }
  function effectKey() {
    return [state.uploadVersion,state.assetVersion,state.x,state.y,state.size,state.brightness].join(':');
  }
  function renderAI() {
    aiButton.disabled = !aiEndpoint || !state.room || !state.assetReady || ai.busy;
    find('[data-action="cancel-ai"]').hidden = !ai.busy;
    find('[data-action="save-ai"]').hidden = !ai.url;
    aiCompare.hidden = !ai.url;
    aiImage.hidden = !ai.url || ai.view !== 'ai';
    root.dataset.aiView = ai.url && ai.view === 'ai' ? 'ai' : 'placement';
    root.dataset.aiBusy = String(ai.busy);
    aiButton.setAttribute('aria-busy', String(ai.busy));
    aiButton.textContent = ai.busy ? 'Creating your lighting…' : ai.url ? 'Create another lighting preview' : 'Create AI lighting preview';
    all('[data-ai-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.aiMode === ai.view)));
  }
  function clearAI(message = '') {
    ++ai.version;
    ai.controller?.abort(); ai.controller = null; ai.busy = false; ai.key = null;
    if (ai.url) URL.revokeObjectURL(ai.url);
    ai.url = null; ai.view = 'placement';
    aiImage.removeAttribute('src'); aiStatus.textContent = message;
    renderAI();
  }
  function invalidateAI() {
    if (ai.key && ai.key !== effectKey()) clearAI('Your placement changed. Create a new lighting preview when you’re ready.');
    else renderAI();
  }
  function captureForAI() {
    const scene = document.createElement('canvas');
    const ratio = Math.min(1, 1536 / Math.max(state.room.naturalWidth,state.room.naturalHeight));
    scene.width = Math.round(state.room.naturalWidth*ratio);
    scene.height = Math.round(state.room.naturalHeight*ratio);
    const layer = document.createElement('canvas'); layer.width=scene.width; layer.height=scene.height;
    const lampHeight = scene.height*state.size/100;
    const lampWidth = lampHeight*state.variant.width/state.variant.height;
    layer.getContext('2d').drawImage(lampImage,scene.width*state.x/100-lampWidth/2,scene.height*state.y/100-lampHeight,lampWidth,lampHeight);
    const context = scene.getContext('2d');
    context.drawImage(state.room,0,0,scene.width,scene.height); context.drawImage(layer,0,0);
    return {scene:scene.toDataURL('image/png').split(',')[1],lamp:layer.toDataURL('image/png').split(',')[1],
      x:state.x,y:state.y,size:state.size,brightness:state.brightness,kind:state.product.kind};
  }
  function waitForAI(milliseconds, signal) {
    return new Promise((resolve,reject)=>{
      if (signal.aborted) return reject(new DOMException('Aborted','AbortError'));
      const stop=()=>{clearTimeout(timer);reject(new DOMException('Aborted','AbortError'));};
      const timer=setTimeout(()=>{signal.removeEventListener('abort',stop);resolve();},milliseconds);
      signal.addEventListener('abort',stop,{once:true});
    });
  }
  async function requestAIEffect(payload, signal) {
    let response=await fetch(aiEndpoint,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload),signal,credentials:'same-origin'});
    let result=await response.json();
    if (!response.ok) throw new Error(result.error || 'The lighting preview could not be generated.');
    // Production queues edits so Shopify's proxy never waits on the image API.
    // The local adapter can also return a finished image immediately.
    if (response.status===202) {
      if (typeof result.job!=='string' || !/^[a-f0-9]{32}$/.test(result.job)) throw new Error('The lighting service returned an invalid request.');
      const jobUrl=new URL(aiEndpoint);
      jobUrl.pathname=jobUrl.pathname.replace(/\/$/,'')+'/jobs/'+result.job;
      while (response.status===202) {
        await waitForAI(2000,signal);
        response=await fetch(jobUrl,{signal,credentials:'same-origin',cache:'no-store'});
        result=await response.json();
        if (!response.ok) throw new Error(result.error || 'The lighting preview could not be generated.');
      }
    }
    return result;
  }
  async function generateAI() {
    if (!aiEndpoint || !state.room || !state.assetReady || ai.busy) return;
    if (state.roomAspect < 1/3 || state.roomAspect > 3) return fail('For AI lighting, choose a room photo with an aspect ratio between 1:3 and 3:1.');
    clearAI(); clearError();
    const version=ai.version; ai.key=effectKey();
    const requestController=new AbortController(); ai.controller=requestController; ai.busy=true;
    aiStatus.textContent='Adding light and reflections. This can take a few minutes. You can keep adjusting; changes stop this preview.';
    renderAI();
    const timer=setTimeout(()=>requestController.abort('timeout'),250000);
    try {
      const result=await requestAIEffect(captureForAI(),requestController.signal);
      if (version!==ai.version || ai.key!==effectKey()) return;
      if (result.mime!=='image/png' || typeof result.image!=='string' || result.image.length>24000000) throw new Error('The image service returned an invalid preview.');
      const bytes=Uint8Array.from(atob(result.image),character=>character.charCodeAt(0));
      const objectUrl=URL.createObjectURL(new Blob([bytes],{type:'image/png'}));
      try { await loadImage(objectUrl); } catch { URL.revokeObjectURL(objectUrl); throw new Error('The lighting preview could not be opened.'); }
      if (version!==ai.version || ai.key!==effectKey()) { URL.revokeObjectURL(objectUrl); return; }
      ai.url=objectUrl; aiImage.src=objectUrl; ai.view='ai'; state.original=false;
      placement();
      aiStatus.textContent='Your AI lighting preview is ready. Compare it with your placement or save the image. Light and reflections are illustrative.';
      say('AI lighting preview ready.');
    } catch (exception) {
      if (version!==ai.version) return;
      ai.key=null;
      aiStatus.textContent='Your room and placement are saved here. You can try again.';
      fail(requestController.signal.aborted ? 'Generation took too long. Please try again later.' : exception instanceof TypeError ? 'The lighting service is unavailable. Please try again later.' : exception.message);
    } finally {
      clearTimeout(timer);
      if (version===ai.version) { ai.busy=false; ai.controller=null; renderAI(); }
    }
  }
  function saveAI() {
    if (!ai.url || ai.key!==effectKey()) return;
    const link=document.createElement('a'); link.href=ai.url; link.download='sibrille-ai-lighting-preview.png'; link.click();
    say('Your AI lighting preview is ready to save.');
  }
  listen(aiButton,'click',generateAI);
  listen(find('[data-action="cancel-ai"]'),'click',()=>clearAI('Stopped waiting. Your placement is kept. An image request already sent may still finish on the service.'));
  listen(find('[data-action="save-ai"]'),'click',saveAI);
  all('[data-ai-mode]').forEach(button=>listen(button,'click',()=>{ai.view=button.dataset.aiMode;state.original=false;placement();renderAI();}));

  function exportPlacement() {
    if (!state.room || !state.assetReady) return;
    const roomVersion = state.uploadVersion;
    const canvas = document.createElement('canvas');
    const ratio = Math.min(1,1600 / Math.max(state.room.naturalWidth,state.room.naturalHeight));
    canvas.width = Math.round(state.room.naturalWidth * ratio);
    canvas.height = Math.round(state.room.naturalHeight * ratio);
    const context = canvas.getContext('2d');
    context.drawImage(state.room,0,0,canvas.width,canvas.height);
    drawLight(context, canvas.width, canvas.height);
    const lampHeight = canvas.height * state.size / 100;
    const lampWidth = lampHeight * state.variant.width / state.variant.height;
    context.drawImage(lampImage,canvas.width * state.x / 100 - lampWidth / 2,canvas.height * state.y / 100 - lampHeight,lampWidth,lampHeight);
    canvas.toBlob(blob => {
      if (roomVersion !== state.uploadVersion) return;
      if (!blob) return fail('The preview could not be saved. Please try again.');
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `sibrille-room-${state.variant.title.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}.png`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url),10000);
      say('Your placement preview is ready to save. Compare finishes or shop your selected lamp.');
      all('[data-step]').forEach(item => item.classList.toggle('active', item.dataset.step === '3'));
    },'image/png');
  }
  function resize(value) { state.size = clamp(Number(value),20,90); state.original = false; placement(); }
  function setBrightness(value) {
    state.brightness = clamp(Number(value),0,100);
    state.original = false; placement();
    say(state.brightness ? `Illustrative brightness set to ${state.brightness}%.` : 'Preview light is off.');
  }
  const actions = {
    upload: () => input.click(), sample: useSample, remove: removeRoom, reset: () => { reset(); say('Placement and brightness reset. Your room and finish are unchanged.'); },
    smaller: () => resize(state.size-3), larger: () => resize(state.size+3),
    dimmer: () => setBrightness(state.brightness-10), brighter: () => setBrightness(state.brightness+10),
    original: () => { state.original = !state.original; placement(); say(state.original ? 'Showing your original room photo.' : 'Showing your room with the selected lamp.'); }, save: exportPlacement,
  };
  all('[data-action]').forEach(button => listen(button, 'click', () => actions[button.dataset.action]?.()));
  listen(input, 'change', () => useFile(input.files[0]));
  listen(sizeInput, 'input', () => resize(sizeInput.value));
  listen(brightnessInput, 'input', () => setBrightness(brightnessInput.value));
  listen(stage, 'dragover',event => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; find('[data-drop]').hidden = false; });
  listen(stage, 'dragleave',event => { if (!stage.contains(event.relatedTarget)) find('[data-drop]').hidden = true; });
  listen(stage, 'drop',event => { event.preventDefault(); find('[data-drop]').hidden = true; useFile(event.dataTransfer.files[0]); });
  function newGesture() {
    const values = [...pointers.values()];
    if (values.length === 1) gesture = {type:'drag',pointer:values[0],x:state.x,y:state.y};
    else if (values.length >= 2) gesture = {type:'pinch',distance:Math.hypot(values[0].x-values[1].x,values[0].y-values[1].y),size:state.size};
    else gesture = null;
  }
  listen(lamp, 'pointerdown',event => {
    if (!state.room || !state.assetReady) return;
    pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});
    lamp.setPointerCapture(event.pointerId);
    state.original = false; newGesture(); placement();
  });
  listen(lamp, 'pointermove',event => {
    if (!pointers.has(event.pointerId) || !gesture) return;
    pointers.set(event.pointerId,{x:event.clientX,y:event.clientY});
    if (gesture.type === 'drag') {
      const bounds = frame.getBoundingClientRect();
      state.x = gesture.x + (event.clientX-gesture.pointer.x) / bounds.width * 100;
      state.y = gesture.y + (event.clientY-gesture.pointer.y) / bounds.height * 100;
      placement();
    } else {
      const [first,second] = [...pointers.values()];
      if (gesture.distance > 0) resize(gesture.size * Math.hypot(first.x-second.x,first.y-second.y) / gesture.distance);
    }
  });
  function endPointer(event) { if (!pointers.has(event.pointerId)) return; pointers.delete(event.pointerId); newGesture(); say('Lamp position updated. Keep adjusting or save your preview.'); }
  listen(lamp, 'pointerup',endPointer); listen(lamp, 'pointercancel',endPointer); listen(lamp, 'lostpointercapture',endPointer);
  listen(lamp, 'keydown',event => {
    if (!state.room) return;
    const moves={ArrowLeft:[-1,0],ArrowRight:[1,0],ArrowUp:[0,-1],ArrowDown:[0,1]};
    if (!moves[event.key]) return;
    event.preventDefault();
    const [x,y]=moves[event.key]; const step=event.shiftKey?5:1;
    state.x+=x*step; state.y+=y*step; state.original=false; placement();
    say('Lamp position updated.');
  });
  listen(window, 'popstate', () => {
    if (!catalog?.products.length) return;
    const selection = requestedSelection();
    selectProduct(selection.product, selection.variant, {updateUrl: false});
  });
  const finishPicker = find('.finish-picker');
  const variantNote = find('[data-variant-note]');
  const mobileQuery = window.matchMedia('(max-width:730px)');
  function arrangeControls() {
    const panel = find('.product-panel');
    const nextAction = find('[data-ai-panel]') || find('.next-action');
    if (mobileQuery.matches) find('[data-mobile-finishes]').append(finishPicker,variantNote);
    else { panel.insertBefore(finishPicker,nextAction); panel.insertBefore(variantNote,nextAction); }
  }
  listen(mobileQuery, 'change',arrangeControls);
  arrangeControls();
  let catalog;
  function requestedSelection() {
    const params = new URLSearchParams(location.search);
    const requestedProduct = params.get('product');
    const requestedVariant = params.get('variant');
    const product = catalog.products.find(item => item.handle === requestedProduct)
      || (!requestedProduct && catalog.products.find(item => item.variants.some(variant => variant.id === requestedVariant)))
      || catalog.products[0];
    const variant = product.variants.find(item => item.id === requestedVariant) || product.variants.find(item => item.available) || product.variants[0];
    return {product, variant};
  }
  async function selectProduct(product, variant, {updateUrl = true} = {}) {
    state.product = {...product, labels: catalog.labels, sampleImage: catalog.sampleImage, currency: catalog.currency};
    root.dataset.selectedProduct = product.handle;
    find('[data-product-select]').value = product.handle;
    find('[data-product-title]').textContent = product.title;
    find('[data-product-category]').textContent = product.category;
    find('[data-product-caption]').textContent = product.heightInches ? `${product.heightInches}″ tall · Check dimensions before ordering.` : product.caption;
    const options = find('[data-finishes]');
    // One delegated handler below avoids retaining listeners from prior products.
    options.replaceChildren();
    product.variants.forEach(item => {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'finish-button'; button.dataset.variantId = item.id;
      button.setAttribute('aria-label', item.title); button.setAttribute('aria-pressed', 'false');
      button.title = item.title;
      const swatch = document.createElement('span'); swatch.setAttribute('aria-hidden', 'true');
      swatch.style.setProperty('--finish-color', item.finish); button.append(swatch); options.append(button);
    });
    pointers.clear(); gesture = null;
    await selectVariant(variant || product.variants.find(item => item.available) || product.variants[0], {updateUrl});
  }
  listen(find('[data-product-select]'), 'change', event => {
    const product = catalog.products.find(item => item.handle === event.target.value);
    if (product) selectProduct(product);
  });
  listen(find('[data-finishes]'), 'click', event => {
    const button = event.target.closest('[data-variant-id]');
    const variant = state.product?.variants.find(item => item.id === button?.dataset.variantId);
    if (variant) selectVariant(variant);
  });
  async function start() {
    try {
      catalog = JSON.parse(find('[data-planner-config]').textContent);
      catalog.products = catalog.products.filter(product => product.variants.length);
      if (!catalog.products.length) throw new Error('no-configured-products');
      const picker = find('[data-product-select]');
      picker.replaceChildren();
      catalog.products.forEach(product => {
        const option = document.createElement('option'); option.value = product.handle; option.textContent = product.title;
        picker.append(option);
      });
      picker.disabled = false;
      const selection = requestedSelection();
      await selectProduct(selection.product, selection.variant, {updateUrl: false});
    } catch { fail('The lamp details could not load. Please reload the page, or use the product link above.'); }
  }
  setReady(); start();
  return () => {
    clearAI();
    controller.abort();
    ++state.uploadVersion; ++state.assetVersion;
    if (state.roomUrl?.startsWith('blob:')) URL.revokeObjectURL(state.roomUrl);
    state.room = null;
  };
}

if (!customElements.get('sibrille-room-planner')) {
  customElements.define('sibrille-room-planner', class extends HTMLElement {
    connectedCallback() {
      if (!this.cleanup && this.querySelector('[data-planner-config]')) this.cleanup = initializeRoomPlanner(this);
    }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
  });
}
